import { useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import { useAuth } from "../hooks/use-auth";
import { updateUserProfileWithAdmin } from "../lib/actions";
import { Loader2 } from "lucide-react";
import { openLinkExternally } from "../lib/open-external";

export function PoliciesModal() {
    const { user, refreshUser } = useAuth();
    const [acceptedDisclaimer, setAcceptedDisclaimer] = useState(false);
    const [acceptedPolicies, setAcceptedPolicies] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // If user is not yet loaded or already accepted, don't show the modal
    if (!user || (user.acceptedDisclaimer && user.acceptedPolicies)) {
        return null;
    }

    const handleAccept = async () => {
        if (!acceptedDisclaimer || !acceptedPolicies) return;
        setIsSubmitting(true);
        try {
            await updateUserProfileWithAdmin(user.id, {
                acceptedDisclaimer: true,
                acceptedPolicies: true,
            });
            await refreshUser();
        } catch (error) {
            console.error("Failed to accept policies", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Dialog open={true}>
            <DialogContent
                className="sm:max-w-[425px] [&>button]:hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            // Hide close button if possible, but DialogContent usually has it. 
            // We'll just rely on the fact that they can't dismiss it by clicking outside or Esc.
            >
                <DialogHeader>
                    <DialogTitle>Važna obavijest</DialogTitle>
                    <DialogDescription>
                        Prije nastavka korištenja aplikacije, molimo vas da pročitate i prihvatite sljedeće uvjete.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                    <div className="flex items-start space-x-2">
                        <Checkbox
                            id="modal-disclaimer"
                            checked={acceptedDisclaimer}
                            onCheckedChange={(checked) => setAcceptedDisclaimer(!!checked)}
                        />
                        <div className="grid gap-1.5 leading-none">
                            <Label
                                htmlFor="modal-disclaimer"
                                className="text-xs font-normal leading-snug cursor-pointer"
                            >
                                Aplikacija nije zamjena za medicinski savjet. Koristite ju na vlastitu odgovornost.
                            </Label>
                        </div>
                    </div>

                    <div className="flex items-start space-x-2">
                        <Checkbox
                            id="modal-policies"
                            checked={acceptedPolicies}
                            onCheckedChange={(checked) => setAcceptedPolicies(!!checked)}
                        />
                        <div className="grid gap-1.5 leading-none">
                            <Label
                                htmlFor="modal-policies"
                                className="text-xs font-normal leading-snug cursor-pointer"
                            >
                                Prihvaćam{" "}
                                <a
                                    href="https://nemaneide.com/nema-neide-aplikacija-pravila-o-privatnosti/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="underline font-semibold hover:text-primary"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openLinkExternally(e);
                                    }}
                                >
                                    Privacy policy i Uvjete korištenja
                                </a>
                                .
                            </Label>
                        </div>
                    </div>
                </div>
                <DialogFooter>
                    <Button
                        onClick={handleAccept}
                        disabled={isSubmitting || !acceptedDisclaimer || !acceptedPolicies}
                        className="w-full bg-accent hover:bg-[#e73e7b]"
                    >
                        {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Prihvati i nastavi
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
